import { apiGet, apiPatch, apiPost, apiPut } from '@/shared/utils/requestBuilder';

import type {
  CreateModeloAndamentoDto,
  ModeloAndamento,
  UpdateModeloAndamentoDto,
  VariavelAndamento,
  VariavelAndamentoApi,
} from '../types/modeloAndamentoTypes';

const BASE = '/admin/arbitral/modelos-andamento';

const extrairMensagemErro = async (response: Response, fallback: string): Promise<string> => {
  const corpo = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (corpo) {
    const erro = corpo.error;
    if (erro && typeof erro === 'object') {
      const message = (erro as Record<string, unknown>).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (typeof erro === 'string' && erro.trim()) return erro;
    if (typeof corpo.message === 'string' && corpo.message.trim()) return corpo.message;
  }
  return fallback;
};

const lerData = async <T>(response: Response, fallback: string): Promise<T> => {
  if (!response.ok) {
    throw new Error(await extrairMensagemErro(response, fallback));
  }
  const result = (await response.json()) as { data: T };
  return result.data;
};

export const modelosAndamentoService = {
  async listar(): Promise<ModeloAndamento[]> {
    const response = await apiGet(`${BASE}?status=TODOS`);
    return lerData<ModeloAndamento[]>(response, 'Erro ao listar modelos de andamento.');
  },

  async listarVariaveis(): Promise<VariavelAndamento[]> {
    const response = await apiGet(`${BASE}/variaveis`);
    const variaveis = await lerData<VariavelAndamentoApi[]>(
      response,
      'Erro ao carregar variáveis.',
    );
    return variaveis.map((variavel) => ({
      key: variavel.key,
      descricao: variavel.label,
      exemplo: variavel.exemplo,
    }));
  },

  async criar(data: CreateModeloAndamentoDto): Promise<ModeloAndamento> {
    const response = await apiPost(BASE, data);
    return lerData<ModeloAndamento>(response, 'Erro ao criar modelo de andamento.');
  },

  async atualizar(id: string, data: UpdateModeloAndamentoDto): Promise<ModeloAndamento> {
    const response = await apiPut(`${BASE}/${id}`, data);
    return lerData<ModeloAndamento>(response, 'Erro ao atualizar modelo de andamento.');
  },

  async alternarStatus(id: string): Promise<ModeloAndamento> {
    const response = await apiPatch(`${BASE}/${id}/status`);
    return lerData<ModeloAndamento>(response, 'Erro ao alterar status do modelo.');
  },
};
